/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { TooltipProps } from '@mui/material';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Button,
  Typography,
  Grid,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import type { TFunction } from 'i18next';
import React from 'react';
import {
  AlertBanner,
  CustomIconButton,
  CustomTooltip,
} from '@opengeoweb/shared';
import type { layerTypes } from '@opengeoweb/store';
import { Add, Delete, Edit, Visibility, Cached } from '@opengeoweb/theme';
import type { layerSelectTypes } from '../../store';
import { isUserAddedService } from '../../store';
import { useLayerSelectTranslation } from '../../utils/i18n';
import { sortByService } from '../LayerSelect/LayerSelectUtils';

interface ServiceOptionsDialogProps {
  services: layerSelectTypes.ActiveServiceObjectEntities;
  layerSelectRemoveService?: (
    serviceId: string,
    serviceUrl: string,
    serviceName: string,
  ) => void;
  layerSelectReloadService?: (serviceUrl: string) => void;
  selectedLayers: layerTypes.ReduxLayer[];
  setServicePopupInfo?: (popupInfo: ServicePopupInfo) => void;
  isLoading?: boolean;
  error?: string;
}

const styles = {
  servicesContainer: {
    width: '360px',
    maxHeight: '412px',
    top: '140px',
    backgroundColor: 'geowebColors.background.surface',
    boxShadow: 6,
    overflow: 'auto',
  },
  header: {
    position: 'sticky',
    top: 0,
    padding: '12px',
    backgroundColor: 'geowebColors.background.surface',
    zIndex: 100,
  },
  text: {
    fontSize: 'default',
    opacity: 0.67,
  },
  loading: {
    position: 'absolute',
    zIndex: 101,
  },
  footer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'sticky',
    bottom: 0,
    backgroundColor: 'geowebColors.background.surface',
    zIndex: 100,
  },
  button: {
    margin: '16px',
    width: '80%',
    fontSize: '12px',
    textTransform: 'none',
  },
};

const stopTabPropagation = (event: React.KeyboardEvent): void => {
  if (event.key === 'Tab') {
    event.stopPropagation();
  }
};

export interface ServicePopupInfo {
  isOpen: boolean;
  serviceId?: string;
  serviceUrl?: string;
  variant: layerSelectTypes.PopupVariant;
}
export const ServiceOptionsDialog: React.FC<ServiceOptionsDialogProps> = ({
  services,
  layerSelectRemoveService,
  layerSelectReloadService,
  selectedLayers,
  setServicePopupInfo,
  isLoading = false,
  error,
}: ServiceOptionsDialogProps) => {
  const { t } = useLayerSelectTranslation();
  const openAddService = (): void => {
    setServicePopupInfo!({ isOpen: true, variant: 'add' });
  };
  const dialogRef = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  return (
    <Box
      data-testid="ServiceDialog"
      onKeyDown={(event): void => {
        if (event.key !== 'Escape') {
          event.stopPropagation();
        }
      }}
      ref={dialogRef}
      sx={styles.servicesContainer}
      tabIndex={-1}
    >
      <Box sx={styles.header}>
        <Box sx={styles.text}>{t('services')}</Box>
        {error ? <AlertBanner shouldClose title={error} /> : null}
      </Box>
      <Backdrop
        open={isLoading}
        sx={{ ...styles.loading, backgroundSize: 'cover' }}
      >
        <CircularProgress />
      </Backdrop>
      <Rows
        layerSelectReloadService={layerSelectReloadService!}
        layerSelectRemoveService={layerSelectRemoveService!}
        selectedLayers={selectedLayers}
        services={services}
        setServicePopupInfo={setServicePopupInfo!}
      />
      <Box sx={styles.footer}>
        <Button
          data-testid="openAddServiceButton"
          onClick={openAddService}
          onKeyDown={stopTabPropagation}
          startIcon={<Add />}
          sx={styles.button}
          variant="tertiary"
        >
          {t('add-a-new-service')}
        </Button>
      </Box>
    </Box>
  );
};

const deleteServiceTitle = (t: TFunction, hasActiveLayers: boolean): string => {
  return hasActiveLayers
    ? t('service-has-active-layers-error')
    : t('delete-service');
};

const tooltipPlacement = (): TooltipProps['placement'] => {
  return 'left';
};

const tooltipPopperProps = {
  disablePortal: true,
  sx: {
    '& .MuiTooltip-tooltip': {
      width: '200px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
  },
};

const Rows: React.FC<{
  services: layerSelectTypes.ActiveServiceObjectEntities;
  layerSelectRemoveService: (
    serviceId: string,
    serviceUrl: string,
    serviceName: string,
  ) => void;
  layerSelectReloadService: (serviceUrl: string) => void;
  setServicePopupInfo: (popupInfo: ServicePopupInfo) => void;
  selectedLayers: layerTypes.ReduxLayer[];
}> = ({
  services,
  layerSelectReloadService,
  layerSelectRemoveService,
  setServicePopupInfo,
  selectedLayers,
}) => {
  const { t } = useLayerSelectTranslation();
  const checkIfServiceHasActiveLayers = (serviceUrl: string): boolean => {
    const foundService = selectedLayers.find(
      (object) => object.service === serviceUrl,
    );
    if (foundService) {
      return true;
    }
    return false;
  };

  const sortedServices = sortByService(services);

  return (
    <List disablePadding>
      {Object.keys(sortedServices).map((serviceId) => {
        const { serviceUrl, serviceName, scope } = services[serviceId];
        const serviceHasActiveLayers = checkIfServiceHasActiveLayers(
          serviceUrl!,
        );
        const userAddedService = isUserAddedService(scope!);
        return (
          <ListItem
            key={serviceUrl}
            secondaryAction={
              <Box
                sx={{
                  marginRight: '0px',
                  width: 96,
                  button: { marginLeft: 1 },
                }}
              >
                <Grid
                  container
                  sx={{
                    justifyContent: 'left',
                    marginRight: '0px',
                    width: 96,
                    button: { marginLeft: 1 },
                  }}
                >
                  <CustomTooltip
                    data-testid="updateServiceTooltip"
                    placement={tooltipPlacement()}
                    slotProps={{ popper: tooltipPopperProps }}
                    title={t('update-service-title')}
                  >
                    <CustomIconButton
                      aria-label="Update layers from WMS service"
                      data-testid="updateServiceButton"
                      onClick={(): void => {
                        layerSelectReloadService(serviceUrl!);
                      }}
                      onKeyDown={stopTabPropagation}
                    >
                      <Cached />
                    </CustomIconButton>
                  </CustomTooltip>
                  {userAddedService ? (
                    <CustomTooltip
                      placement={tooltipPlacement()}
                      slotProps={{ popper: tooltipPopperProps }}
                      title={t('edit-service-title')}
                    >
                      <span>
                        <CustomIconButton
                          aria-label="edit service"
                          data-testid="openEditServiceButton"
                          onClick={(): void => {
                            setServicePopupInfo({
                              isOpen: true,
                              serviceId,
                              serviceUrl,
                              variant: 'edit',
                            });
                          }}
                          onKeyDown={stopTabPropagation}
                        >
                          <Edit />
                        </CustomIconButton>
                      </span>
                    </CustomTooltip>
                  ) : (
                    <CustomTooltip
                      placement={tooltipPlacement()}
                      slotProps={{ popper: tooltipPopperProps }}
                      title={t('show-service-title')}
                    >
                      <span>
                        <CustomIconButton
                          aria-label="show service"
                          data-testid="openShowServiceButton"
                          onClick={(): void => {
                            setServicePopupInfo({
                              isOpen: true,
                              serviceId,
                              serviceUrl,
                              variant: 'show',
                            });
                          }}
                          onKeyDown={stopTabPropagation}
                        >
                          <Visibility />
                        </CustomIconButton>
                      </span>
                    </CustomTooltip>
                  )}
                  {!!userAddedService && (
                    <Box>
                      <CustomTooltip
                        data-testid="removeServiceTooltip"
                        placement={tooltipPlacement()}
                        slotProps={{ popper: tooltipPopperProps }}
                        title={deleteServiceTitle(t, serviceHasActiveLayers)}
                      >
                        <span>
                          <CustomIconButton
                            aria-label="delete service"
                            data-testid="removeServiceButton"
                            disabled={serviceHasActiveLayers}
                            onClick={(): void =>
                              layerSelectRemoveService(
                                serviceId,
                                serviceUrl!,
                                serviceName!,
                              )
                            }
                            onKeyDown={stopTabPropagation}
                          >
                            <Delete />
                          </CustomIconButton>
                        </span>
                      </CustomTooltip>
                    </Box>
                  )}
                </Grid>
              </Box>
            }
          >
            <ListItemText
              style={{
                fontSize: '16px',
              }}
            >
              <Typography
                noWrap
                style={{
                  textOverflow: 'ellipsis',
                  paddingRight: '70px',
                }}
              >
                {serviceName}
              </Typography>
            </ListItemText>
          </ListItem>
        );
      })}
    </List>
  );
};
